'use client';

import Link from 'next/link';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import Headroom from 'react-headroom';
import {
  Anchor,
  Box,
  Burger,
  Button,
  Container,
  Divider,
  Drawer,
  Group,
  ScrollArea,
  Stack,
  Text,
  rem,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { type Locale } from '../../i18n/locales';
import type { SiteSettingsData } from '../../types/content';
import { resolveHref } from '../../lib/content/localization';
import { getCarComparisonLinkProps, mapCarComparisonHref } from '../../lib/content/comparisonLinks';
import { STORAGE_ASSET_URLS } from '../../lib/content/storageAssets';

const resolveCtaHref = (
  locale: Locale,
  href: string,
  options: { includeLegacyAutovakuutus?: boolean } = {},
) => {
  const normalizedHref = normalizeAdminHrefForCurrentEnvironment(href);
  const mappedHref = mapCarComparisonHref(normalizedHref, options) || normalizedHref;
  if (mappedHref.startsWith('/admin')) return mappedHref;
  if (/^[a-zA-Z][a-zA-Z\d+.-]*:/.test(mappedHref) || mappedHref.startsWith('//')) {
    return mappedHref;
  }
  return resolveHref(locale, mappedHref);
};

const normalizeAdminHrefForCurrentEnvironment = (href: string) => {
  try {
    const parsed = new URL(href);
    if (parsed.pathname.startsWith('/admin')) {
      return `${parsed.pathname}${parsed.search}${parsed.hash}`;
    }
  } catch {
    // Keep non-absolute values as-is.
  }
  return href;
};

const stripLocalePrefix = (pathname: string) => pathname.replace(/^\/(fi|sv|en)(?=\/|$)/, '') || '/';

const normalizePath = (value: string) => {
  const path = value.split('?')[0]?.split('#')[0] || '/';
  return path.replace(/\/+$/, '') || '/';
};

const loginCtaLabels = new Set(['kirjaudu', 'kirjaudu sisaan', 'logga in', 'log in', 'login', 'sign in']);

const normalizeCtaLabel = (value?: string) =>
  (value || '')
    .trim()
    .toLocaleLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

const isLoginCtaLabel = (value?: string) => loginCtaLabels.has(normalizeCtaLabel(value));

const normalizeHrefPath = (href?: string) => {
  if (!href) return '';
  try {
    return new URL(href, 'https://vakuutuslaskuri.fi').pathname || '';
  } catch {
    return href;
  }
};

const isAdminAreaHref = (href?: string) => {
  const normalizedHref = normalizeHrefPath(normalizeAdminHrefForCurrentEnvironment(href || ''));
  return normalizedHref.startsWith('/admin');
};

export function Header({ locale, settings }: { locale: Locale; settings: SiteSettingsData }) {
  const [drawerOpened, { toggle: toggleDrawer, close: closeDrawer }] = useDisclosure(false);
  const pathname = usePathname();
  const links = settings.navigation || [];

  const primaryLinks = links
    .map((link) => ({ href: link.href, label: link.label }))
    .filter((link) => Boolean(link.href && link.label));

  const siteTitle = settings.siteTitle || '';
  const primaryCtaLabel = settings.headerCtaLabel || settings.primaryCtaLabel;
  const primaryCtaHref = settings.headerCtaHref || settings.primaryCtaHref;
  const secondaryCtaLabel = settings.headerSecondaryLabel;
  const secondaryCtaHref = settings.headerSecondaryHref;
  const resolvedSecondaryCtaHref = secondaryCtaHref ? resolveCtaHref(locale, secondaryCtaHref) : undefined;
  const resolvedPrimaryCtaHref = primaryCtaHref
    ? resolveCtaHref(locale, primaryCtaHref, { includeLegacyAutovakuutus: true })
    : undefined;
  const currentPath = pathname || resolveHref(locale, '/');
  const normalizedCurrentPath = normalizePath(stripLocalePrefix(currentPath));
  const shouldHideSecondaryCta = isLoginCtaLabel(secondaryCtaLabel) || isAdminAreaHref(secondaryCtaHref);
  const shouldShowSecondaryCta = Boolean(secondaryCtaLabel && resolvedSecondaryCtaHref) && !shouldHideSecondaryCta;
  const shouldShowPrimaryCta = Boolean(primaryCtaLabel && primaryCtaHref);
  const isActiveLink = (href: string) => {
    const normalizedHref = normalizePath(stripLocalePrefix(href));
    if (normalizedHref === '/') return normalizedCurrentPath === '/';
    return normalizedCurrentPath === normalizedHref || normalizedCurrentPath.startsWith(`${normalizedHref}/`);
  };
  return (
    <>
      <Headroom style={{ zIndex: 120 }} wrapperStyle={{ zIndex: 120 }}>
        <Box
          component="header"
          style={{
            background: 'rgba(255,255,255,0.94)',
            backdropFilter: 'blur(6px)',
            borderBottom: '1px solid var(--surface-border)',
          }}
        >
          <Container size="xl" h={84}>
            <Group h="100%" justify="space-between" wrap="nowrap" gap="sm">
              <Group gap="sm" wrap="nowrap" style={{ minWidth: 180 }}>
                <Box
                  component={Link}
                  href={resolveHref(locale, '/')}
                  style={{ display: 'inline-flex', alignItems: 'center' }}
                >
                  <Image
                    src={STORAGE_ASSET_URLS.logo}
                    alt={siteTitle}
                    width={173}
                    height={48}
                    priority
                    unoptimized
                    style={{ height: 48, width: 'auto', objectFit: 'contain' }}
                  />
                </Box>
              </Group>

              <Group gap={2} visibleFrom="md" style={{ flex: 1, justifyContent: 'center' }}>
                {primaryLinks.map((link) => {
                  const href = resolveCtaHref(locale, link.href);
                  const active = isActiveLink(resolveHref(locale, link.href));
                  const linkProps = getCarComparisonLinkProps(href);
                  return (
                    <Anchor
                      key={`${link.href}-${link.label}`}
                      component={Link}
                      href={href}
                      {...linkProps}
                      underline="never"
                      px={12}
                      py={6}
                      aria-current={active ? 'page' : undefined}
                      style={{
                        color: 'var(--text-primary)',
                        borderRadius: 999,
                        fontSize: 14,
                        fontWeight: active ? 700 : 500,
                        background: active ? 'var(--surface-soft)' : 'transparent',
                        border: active ? '1px solid var(--surface-border)' : '1px solid transparent',
                      }}
                    >
                      {link.label}
                    </Anchor>
                  );
                })}
              </Group>

              <Group visibleFrom="md" gap="xs" wrap="nowrap" style={{ minWidth: 220, justifyContent: 'flex-end' }}>
                {shouldShowSecondaryCta && resolvedSecondaryCtaHref ? (
                  <Button
                    component={Link}
                    href={resolvedSecondaryCtaHref}
                    {...getCarComparisonLinkProps(resolvedSecondaryCtaHref)}
                    variant="default"
                    color="gray"
                    styles={{
                      root: {
                        background: 'var(--surface-soft)',
                        borderColor: 'var(--surface-border)',
                        color: 'var(--text-primary)',
                      },
                    }}
                  >
                    {secondaryCtaLabel}
                  </Button>
                ) : null}
                {shouldShowPrimaryCta && primaryCtaHref ? (
                  <Button
                    component={Link}
                    href={resolvedPrimaryCtaHref || primaryCtaHref}
                    {...getCarComparisonLinkProps(resolvedPrimaryCtaHref)}
                    styles={{
                      root: {
                        background: 'var(--brand-primary)',
                        color: '#fff',
                        borderColor: 'transparent',
                      },
                    }}
                  >
                    {primaryCtaLabel}
                  </Button>
                ) : null}
              </Group>

              <Burger
                opened={drawerOpened}
                onClick={toggleDrawer}
                hiddenFrom="md"
                size="sm"
                aria-label={
                  locale === 'fi'
                    ? drawerOpened
                      ? 'Sulje valikko'
                      : 'Avaa valikko'
                    : locale === 'sv'
                      ? drawerOpened
                        ? 'Stäng meny'
                        : 'Öppna meny'
                      : drawerOpened
                        ? 'Close menu'
                        : 'Open menu'
                }
              />
            </Group>
          </Container>
        </Box>
      </Headroom>

      <Drawer
        opened={drawerOpened}
        onClose={closeDrawer}
        size="100%"
        padding="md"
        title={
          <Group gap="sm" wrap="nowrap">
            <Box component={Link} href={resolveHref(locale, '/')} onClick={closeDrawer}>
              <Image
                src={STORAGE_ASSET_URLS.logo}
                alt={siteTitle}
                width={123}
                height={34}
                unoptimized
                style={{ height: 34, width: 'auto', objectFit: 'contain' }}
              />
            </Box>
          </Group>
        }
        hiddenFrom="md"
        zIndex={1000000}
      >
        <ScrollArea h={`calc(100vh - ${rem(86)})`} mx="-md">
          <Divider my="sm" />
          <Stack px="md" gap="xs">
            {primaryLinks.map((link) => {
              const href = resolveCtaHref(locale, link.href);
              const active = isActiveLink(resolveHref(locale, link.href));
              const linkProps = getCarComparisonLinkProps(href);
              return (
                <Anchor
                  key={link.href}
                  component={Link}
                  href={href}
                  {...linkProps}
                  onClick={closeDrawer}
                  underline="never"
                  p={10}
                  aria-current={active ? 'page' : undefined}
                  style={{
                    borderRadius: 999,
                    border: '1px solid var(--surface-border)',
                    color: 'var(--text-primary)',
                    fontWeight: active ? 700 : 500,
                    background: active ? 'var(--surface-soft)' : 'transparent',
                  }}
                >
                  {link.label}
                </Anchor>
              );
            })}
          </Stack>

          <Divider my="md" />

          <Stack pb="xl" px="md" gap="sm">
            {shouldShowSecondaryCta && resolvedSecondaryCtaHref ? (
              <Button
                variant="default"
                fullWidth
                component={Link}
                href={resolvedSecondaryCtaHref}
                {...getCarComparisonLinkProps(resolvedSecondaryCtaHref)}
                onClick={closeDrawer}
                styles={{
                  root: {
                    background: 'var(--surface-soft)',
                    borderColor: 'var(--surface-border)',
                    color: 'var(--text-primary)',
                  },
                }}
              >
                {secondaryCtaLabel}
              </Button>
            ) : null}
            {shouldShowPrimaryCta && primaryCtaHref ? (
              <Button
                fullWidth
                component={Link}
                href={resolvedPrimaryCtaHref || primaryCtaHref}
                {...getCarComparisonLinkProps(resolvedPrimaryCtaHref)}
                onClick={closeDrawer}
                styles={{
                  root: {
                    background: 'var(--brand-primary)',
                    color: '#fff',
                  },
                }}
              >
                {primaryCtaLabel}
              </Button>
            ) : null}
            {siteTitle ? (
              <Text size="xs" c="dimmed" ta="center" mt="xs">
                {siteTitle}
              </Text>
            ) : null}
          </Stack>
        </ScrollArea>
      </Drawer>
    </>
  );
}
